import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Loader2, ArrowLeft, ArrowRight, Check, User, Activity,
  Heart, Bone, ClipboardList, AlertTriangle, CheckCircle2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  Step1PersonalData,
  Step3SafetyEvaluation,
  Step4Activity,
  Step5Limitations,
  Step6RiskFactors,
} from './NewEvaluationPage';

// 5 pasos que replican el wizard del profesional (Fase C).
// Grupos y "Estudios complementarios" (Step2 del profe) NO aplican al alumno.
// Orden alineado al árbol ACSM (Riebe et al., 2015): Actividad → Enfermedades → Síntomas.
const STEPS = [
  { id: 1, title: 'Datos personales', icon: User },
  { id: 2, title: 'Actividad física habitual', icon: Activity },
  { id: 3, title: 'Factores de riesgo y enfermedades', icon: ClipboardList },
  { id: 4, title: 'Signos y síntomas', icon: Heart },
  { id: 5, title: 'Limitaciones', icon: Bone },
];

const initialFormData = {
  // Step 1
  student_name: '', age: '', sex: '', weight: '', height: '',
  waist_circumference: '', resting_heart_rate: '',
  blood_pressure_systolic: '', blood_pressure_diastolic: '',
  // Step 4 — Síntomas (null = sin responder)
  symptom_chest_pain: null, symptom_neck_jaw_arm_pain: null,
  symptom_dyspnea: null, symptom_dizziness: null,
  symptom_orthopnea: null, symptom_ankle_swelling: null,
  symptom_palpitations: null, symptom_intermittent_claudication: null,
  symptom_heart_murmur: null, symptom_unusual_fatigue: null,
  // Step 2 — Actividad
  activity_level: '',
  does_planned_exercise: null,
  act_moderate_intensity: false,
  act_min_30: false,
  act_freq_3: false,
  act_sustained_3mo: false,
  // Step 5 — Limitaciones
  has_limitations: false, affected_joint: '',
  limitation_joints: [], pain_scale: 0, pain_type: '', limitation_observations: '',
  // Step 3 — Factores de riesgo y enfermedades
  cardiac_disease: null, cerebrovascular_disease: null,
  peripheral_artery_disease: null, chronic_kidney_disease: null,
  diabetes: null, diabetes_type: '', diabetes_treatment: null, prediabetes: null,
  hypertension: null, hypertension_treatment: null,
  dyslipidemia: null, dyslipidemia_treatment: null,
  smoking: null, family_history: null,
};

function InvalidTokenScreen({ reason }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <Card className="max-w-md w-full border-slate-200" data-testid="intake-invalid-screen">
        <CardContent className="pt-8 pb-8 text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-amber-100 mx-auto flex items-center justify-center">
            <AlertTriangle className="h-7 w-7 text-amber-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-800 font-['Inter']">Este link ya no está disponible</h1>
          <p className="text-sm text-slate-600 font-['Inter']">
            Pedile uno nuevo a tu profesor para completar tu entrevista.
          </p>
          <p className="text-xs text-slate-400 font-['Inter']" data-testid="intake-invalid-reason">
            {reason || 'Link inválido o vencido'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function ThankYouScreen({ professionalName }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <Card className="max-w-md w-full border-emerald-200 bg-emerald-50" data-testid="intake-thank-you">
        <CardContent className="pt-8 pb-8 text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-emerald-200 mx-auto flex items-center justify-center">
            <CheckCircle2 className="h-7 w-7 text-emerald-700" />
          </div>
          <h1 className="text-xl font-bold text-emerald-900 font-['Inter']">¡Gracias!</h1>
          <p className="text-sm text-emerald-800 font-['Inter']">
            Tu profesor va a revisarla.
          </p>
          {professionalName && (
            <p className="text-xs text-emerald-700/80 font-['Inter']">
              {professionalName}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function StudentIntakePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [errorReason, setErrorReason] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const info = await api.getStudentIntakeInfo(token);
        setTokenInfo(info);
      } catch (e) {
        setErrorReason(e.message || 'Link inválido');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const progress = (currentStep / STEPS.length) * 100;

  const canProceed = () => {
    if (currentStep === 1) {
      return formData.student_name && formData.age && formData.sex &&
             formData.weight && formData.height;
    }
    if (currentStep === 2) return !!formData.activity_level;
    return true;
  };

  const handleNext = () => {
    if (!canProceed()) {
      toast.error('Completá los campos obligatorios');
      return;
    }
    if (currentStep < STEPS.length) setCurrentStep(currentStep + 1);
  };

  const handlePrev = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    if (!consentAccepted) {
      toast.error('Tenés que aceptar el consentimiento para enviar');
      return;
    }
    setSubmitting(true);
    try {
      // Solo enviar los campos que el endpoint público acepta (PartialEvaluationCreate)
      const payload = {
        student_name: formData.student_name,
        age: parseInt(formData.age, 10),
        sex: formData.sex,
        weight: parseFloat(formData.weight),
        height: parseFloat(formData.height),
        blood_pressure_systolic: formData.blood_pressure_systolic ? parseInt(formData.blood_pressure_systolic, 10) : null,
        blood_pressure_diastolic: formData.blood_pressure_diastolic ? parseInt(formData.blood_pressure_diastolic, 10) : null,
        activity_level: formData.activity_level,
        does_planned_exercise: formData.does_planned_exercise,
        act_moderate_intensity: formData.act_moderate_intensity === true,
        act_min_30: formData.act_min_30 === true,
        act_freq_3: formData.act_freq_3 === true,
        act_sustained_3mo: formData.act_sustained_3mo === true,
        symptom_chest_pain: formData.symptom_chest_pain === true,
        symptom_neck_jaw_arm_pain: formData.symptom_neck_jaw_arm_pain === true,
        symptom_dyspnea: formData.symptom_dyspnea === true,
        symptom_dizziness: formData.symptom_dizziness === true,
        symptom_orthopnea: formData.symptom_orthopnea === true,
        symptom_ankle_swelling: formData.symptom_ankle_swelling === true,
        symptom_palpitations: formData.symptom_palpitations === true,
        symptom_intermittent_claudication: formData.symptom_intermittent_claudication === true,
        symptom_heart_murmur: formData.symptom_heart_murmur === true,
        symptom_unusual_fatigue: formData.symptom_unusual_fatigue === true,
        cardiac_disease: formData.cardiac_disease === true,
        cerebrovascular_disease: formData.cerebrovascular_disease === true,
        peripheral_artery_disease: formData.peripheral_artery_disease === true,
        chronic_kidney_disease: formData.chronic_kidney_disease === true,
        diabetes: formData.diabetes === true,
        diabetes_type: formData.diabetes === true ? (formData.diabetes_type || null) : null,
        prediabetes: formData.diabetes === true ? false : formData.prediabetes === true,
        has_limitations: !!formData.has_limitations,
        affected_joint: formData.has_limitations
          ? (Array.isArray(formData.limitation_joints) && formData.limitation_joints.length > 0
              ? formData.limitation_joints.join(', ')
              : (formData.affected_joint || null))
          : null,
        consent_accepted: true,
      };
      await api.submitStudentIntake(token, payload);
      setSubmitted(true);
    } catch (e) {
      toast.error(e.message || 'No se pudo enviar. Intentá de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" data-testid="intake-loading" />
      </div>
    );
  }

  if (errorReason || !tokenInfo) {
    return <InvalidTokenScreen reason={errorReason} />;
  }

  if (submitted) {
    return <ThankYouScreen professionalName={tokenInfo.professional_name} />;
  }

  const StepIcon = STEPS[currentStep - 1].icon;
  const isLastStep = currentStep === STEPS.length;

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="student-intake-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <p className="text-xs text-slate-500 uppercase tracking-wider font-medium font-['Inter']">
            Entrevista previa
          </p>
          <h1 className="text-lg sm:text-xl font-bold text-slate-800 font-['Inter']" data-testid="intake-professional-name">
            {tokenInfo.professional_name}
          </h1>
        </div>
        <div className="max-w-2xl mx-auto px-4 pb-3">
          <div className="flex items-center justify-between text-xs mb-2 font-['Inter']">
            <span className="text-slate-500">Paso {currentStep} de {STEPS.length}</span>
            <span className="font-medium text-slate-700">{STEPS[currentStep - 1].title}</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <Card className="border-slate-200 bg-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-5">
              <StepIcon className="h-5 w-5 text-cyan-600" />
              <h2 className="text-base font-semibold text-slate-800 font-['Inter']">
                {STEPS[currentStep - 1].title}
              </h2>
            </div>

            {currentStep === 1 && (
              <Step1PersonalData
                formData={formData}
                updateField={updateField}
                hideGroups={true}
                hideResults={true}
              />
            )}
            {currentStep === 2 && (
              <Step4Activity formData={formData} updateField={updateField} />
            )}
            {currentStep === 3 && (
              <Step6RiskFactors formData={formData} updateField={updateField} />
            )}
            {currentStep === 4 && (
              <Step3SafetyEvaluation formData={formData} updateField={updateField} hideMedicalAlert={true} />
            )}
            {currentStep === 5 && (
              <Step5Limitations formData={formData} updateField={updateField} />
            )}

            {/* Consentimiento — visible en el último paso */}
            {isLastStep && (
              <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-lg" data-testid="intake-consent-block">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="intake-consent"
                    checked={consentAccepted}
                    onCheckedChange={(v) => setConsentAccepted(v === true)}
                    data-testid="intake-consent-checkbox"
                    className="mt-1"
                  />
                  <Label htmlFor="intake-consent" className="text-sm text-slate-700 font-normal leading-relaxed cursor-pointer font-['Inter']">
                    Acepto que los datos que cargué se compartan con mi profesor para el análisis
                    de esta entrevista previa al ejercicio (Ley 25.326 de Protección de Datos Personales).
                  </Label>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Navigation bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 z-10">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={currentStep === 1 || submitting}
            className="font-['Inter']"
            data-testid="intake-prev-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Atrás
          </Button>
          {!isLastStep ? (
            <Button
              onClick={handleNext}
              className="bg-[#1F3A5F] hover:bg-[#2a4a73] font-['Inter']"
              data-testid="intake-next-btn"
            >
              Siguiente
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting || !consentAccepted}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-['Inter'] disabled:opacity-50"
              data-testid="intake-submit-btn"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enviando...</>
              ) : (
                <><Check className="h-4 w-4 mr-2" />Enviar entrevista</>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
